import { PureComponent } from 'react';
import { DefaultTheme, ThemeProps, withTheme } from 'styled-components';
import { getThemeValue, handleRefSplit } from '@optimus-web/common';
import { ListItemVariant } from '@optimus-web/local-tokens';
import { Checkbox as CheckboxComponent, CheckboxProps } from '../Checkbox';
import { Identified, InputValue, Labeled, Valued } from '../types';
import {
    AvatarElement,
    CheckboxWrapper,
    ContentElement,
    IconElement,
    IconElementProps,
    LeftContentElement,
    ListItemContainerElement,
    PrimaryTextElement,
    PrimaryTextElementProps,
    RightContentElement,
    SecondaryTextElement,
} from './style';

export interface ListItemFactory {
    Avatar?: React.ComponentType;
    Content?: React.ComponentType;
    Checkbox?: React.ComponentType<CheckboxProps>;
    Icon?: React.ComponentType<IconElementProps>;
    LeftContent?: React.ComponentType;
    ListItemContainer?: React.ComponentType;
    PrimaryText?: React.ComponentType<PrimaryTextElementProps | {}>;
    RightContent?: React.ComponentType;
    SecondaryText?: React.ComponentType;
}

export interface ListItemProps extends Partial<Valued>, Labeled, Partial<Identified> {
    onAfterClick?: (e: React.MouseEvent, v?: InputValue) => void;
    focused?: boolean;
    onClick?: (e: React.MouseEvent, v?: InputValue) => void;
    selected?: boolean;
    onMouseEnter?: () => void;
    inactive?: boolean;
    indent?: boolean;
    shouldScrollIntoView?: boolean;
    dense?: boolean;
    leftAvatar?: React.ReactNode;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    multiselect?: boolean;
    keyboardHandled?: boolean;
    wrapText?: boolean;
    disabled?: boolean;
    depth?: number;
    leftItem?: React.ReactNode;
    rightItem?: React.ReactNode;
    innerRef?: (c: HTMLElement) => void;
    tabIndex?: number;
    role?: string;
    'aria-selected'?: boolean;
    variant?: ListItemVariant;
}

export const listItemFactory = ({
    Avatar = AvatarElement,
    Content = ContentElement,
    Checkbox = CheckboxComponent,
    Icon = IconElement,
    LeftContent = LeftContentElement,
    ListItemContainer = ListItemContainerElement,
    PrimaryText = PrimaryTextElement,
    RightContent = RightContentElement,
    SecondaryText = SecondaryTextElement,
}: ListItemFactory = {}) => {
    class ListItem extends PureComponent<ListItemProps & ThemeProps<DefaultTheme>> {
        static defaultProps: Partial<ListItemProps> = {
            wrapText: false,
            variant: 'default',
        };

        private container?: HTMLElement;

        componentDidUpdate(prevProps: ListItemProps) {
            if (!prevProps.focused && this.props.focused && this.props.shouldScrollIntoView && this.container) {
                this.container.scrollIntoView(false);
            }
        }

        handleClick = (e: React.MouseEvent) => {
            if (this.props.onClick) {
                this.props.onClick(e, this.props.value);
            }

            if (this.props.onAfterClick) {
                this.props.onAfterClick(e, this.props.value);
            }
        };

        handleContainerRef = (c: HTMLElement) => {
            this.container = c;
        };

        render() {
            const {
                leftAvatar,
                children,
                dense: customDense,
                primaryText,
                secondaryText,
                leftIcon,
                rightIcon,
                selected,
                multiselect,
                wrapText,
                leftItem,
                rightItem,
                onAfterClick,
                innerRef,
                onMouseEnter,
                keyboardHandled,
                disabled,
                inactive,
                indent,
                variant,
                theme,
                ...other
            } = this.props;

            const { dense } = getThemeValue(theme, 'listItem', (ctxDense) => ({
                dense: customDense ?? ctxDense.dense,
            }));

            const checkBoxWrapper = (
                <CheckboxWrapper left>
                    <Checkbox tabIndex={-1} checked={!!selected} name={this.props.id} value={this.props.value} />
                </CheckboxWrapper>
            );

            const left =
                leftItem ||
                (leftAvatar && <Avatar>{leftAvatar}</Avatar>) ||
                (leftIcon && <Icon left>{leftIcon}</Icon>) ||
                (multiselect && checkBoxWrapper);

            const right = rightItem || (rightIcon && <Icon right>{rightIcon}</Icon>);

            const containerProps = {
                ...other,
                ref: handleRefSplit({ ownRef: this.handleContainerRef, forwardedRef: innerRef }),
                dense,
                wrapText,
                selected,
                singleLineWithAvatar: !!leftAvatar && !secondaryText,
                twoLine: !!secondaryText,
                onClick: !disabled ? this.handleClick : undefined,
                onMouseEnter: !disabled ? this.props.onMouseEnter : undefined,
                keyboardHandled,
                disabled,
                inactive,
                indent,
                'data-list-container': true,
            };

            return (
                <ListItemContainer {...containerProps}>
                    {left && <LeftContent>{left}</LeftContent>}
                    <Content>
                        <PrimaryText dense={!!dense} wrapText={!!wrapText} variant={variant} data-test-primary-text>
                            {primaryText}
                        </PrimaryText>
                        {secondaryText && <SecondaryText data-test-secondary-text>{secondaryText}</SecondaryText>}
                    </Content>
                    {right && <RightContent>{right}</RightContent>}
                    {children}
                </ListItemContainer>
            );
        }
    }

    return withTheme(ListItem);
};

export default listItemFactory();
